import React, { useState } from 'react';
import type { StageActionLog } from './SubmissionRow';

const STAGE_LABEL: Record<string, string> = {
  '': 'PreSelection',
  preselection: 'PreSelection',
  qualification: 'Qualification',
  final: 'Final',
};

const stageLabel = (s: string) => STAGE_LABEL[s] ?? s;

const ActionLogPanel = ({ logs }: { logs: StageActionLog[] }) => {
  const [collapsed, setCollapsed] = useState(false);

  if (logs.length === 0) return null;

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="mt-8 mb-2 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Panel header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {/* Activity icon */}
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
            <svg
              className="w-3.5 h-3.5 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </span>
          <span className="font-bai font-semibold text-sm text-gray-800">
            Action Log
          </span>
          <span className="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-[10px] font-bold text-white font-baijamjuree">
            {logs.length}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Log entries */}
      {!collapsed && (
        <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
          {[...logs].reverse().map(log => {
            const isAdvance = log.action === 'advance';
            return (
              <div
                key={log.id}
                className="flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                {/* Action badge */}
                <div className="flex-shrink-0 mt-0.5">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-baijamjuree font-semibold uppercase tracking-wide ${
                      isAdvance
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {isAdvance ? (
                      <>
                        <svg
                          className="w-2.5 h-2.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                        Advance
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-2.5 h-2.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                          />
                        </svg>
                        Revert
                      </>
                    )}
                  </span>
                </div>

                {/* Main content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 font-bai leading-snug">
                    <span className="font-semibold">{log.actor_name}</span>
                    <span className="text-gray-500 text-xs ml-1">
                      ({log.actor_email})
                    </span>
                    <span className="text-gray-600">
                      {' '}
                      {isAdvance ? 'advanced' : 'reverted'}{' '}
                    </span>
                    <span className="font-semibold text-gray-900">
                      "{log.submission_name}"
                    </span>
                  </p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {/* Stage transition */}
                    <span className="inline-flex items-center gap-1 text-[11px] font-baijamjuree text-gray-500">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                        {stageLabel(log.previous_stage)}
                      </span>
                      <svg
                        className="w-3 h-3 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                        />
                      </svg>
                      <span
                        className={`rounded px-1.5 py-0.5 font-semibold ${
                          isAdvance
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {stageLabel(log.new_stage)}
                      </span>
                    </span>
                    {/* Pillar dot */}
                    <span className="text-gray-300">·</span>
                    <span className="text-[11px] font-baijamjuree text-gray-500">
                      {log.submission_pillar}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span className="text-[11px] font-baijamjuree text-gray-500">
                      {log.submission_category}
                    </span>
                  </div>
                </div>

                {/* Timestamp */}
                <div className="flex-shrink-0 text-right">
                  <p className="text-[11px] font-baijamjuree font-semibold text-gray-700">
                    {formatTime(log.timestamp)}
                  </p>
                  <p className="text-[10px] font-baijamjuree text-gray-400">
                    {formatDate(log.timestamp)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionLogPanel;
